import { Model } from './navmodel';
import { NavPropertyItem } from '../navdata/navpropertyitem';
import { NavPageMetaData } from '../navdata/navmodel';
import { NavPropertyContainer } from '@common/navdata';
/** Describes RAW Page WebService Item Data */
export interface IPageObj {
    /** Key is always available for Server Read Item */
    Key?: string;
}

declare global {
    /** global interface to publish page Maps on  */
    export interface INavPageMap {

    }
}
export type TDefaultPageObj = any;

type FilterFlags<Base, Condition> = {
    [Key in keyof Base]: Base[Key] extends Condition ? Key : never
};
type AllowedNames<Base, Condition> = FilterFlags<Base, Condition>[keyof Base];
type SubType<Base, Condition> = Pick<Base, AllowedNames<Base, Condition>>;

type TDefinedPageInfos = FilterFlags<INavPageMap, IPageObj>;

/** explicitly Defined Page Names with typed Obj */
export type TDefinedPageName = AllowedNames<TDefinedPageInfos, string>;

/** explicitly Defined Page Maps */
export type TDefinedPageMaps = SubType<INavPageMap, IPageObj>;

/** Property Grid Visibility Behavior */
export enum NavGridVisibility {
    /// <summary> Determined from "Visible" Setting </summary>
    Default,
    /// <summary> Property is visible, but can be made hidden by user </summary>
    InitialVisible,
    /// <summary> Property is hidden, but can be made visible by user </summary>
    InitialHidden,
    /// <summary> Column Is Visible and user can not remove the Property</summary>
    ForcedVisible,
    /// <summary> Column gets removed from Visible Columns</summary>
    ForcedRemove
}

/** ReadMultiple Container SyncMode */
export enum NavSyncMode {
    /** Do Nothing */
    None = 0,
    /** Sync ValueChange to Other Items */
    Sync = 1
}

/// <summary> DateTime format options</summary>
export enum NavDtFormat {
    /// <summary> Default: right now : Date only </summary>
    Default,
    /// <summary> Display with Date and Time </summary>
    DateAndTime,
    /// <summary> Display Time only </summary>
    Time
}

/** Possible Types for UI Line Item */
export enum DMSUILineType {
    Message = 0,
    Confirm = 1,
    StrMenu = 2,
}

/** Numeric Type  for Type="Decimal"*/
export enum NumericType {
    Number = 0,
    Currency = 1,
    Percent = 2,
}

/** Enum Mappings */
export interface IEnumMapping {
    /** Display Title */
    Title: string;
    /** Enum Value Name */
    Enum: string;
    /** Number Value */
    Value: number;
    /** Value Should not be visualized */
    Invalid?: boolean;
    /** Explicit Enum Value Considered to be empty */
    IsEmpty?: boolean;
}

// Enum Maping Definition
export interface IEnumMappings extends Array<IEnumMapping> {
    /** Client Side Attached 'Empty' State if any (the Mapping with value == '_blank_' or Explicit ) */
    Empty?: IEnumMapping;

    _TemplateIgnore?: true;
}

/** Zugriff auf Eigenschaften eines Page-Objektes : über Model-Key oder explizite Property */
export type TPagePropertyAccess<TPage = TDefaultPageObj> = keyof TPage | IPageProperty;
export type TPagePropRefs<TPage = TDefaultPageObj> = TPagePropertyAccess<TPage> | TPagePropertyAccess<TPage>[] | "*";

export type PagePropertyType = "String" | "Boolean" | "DateTime" | "Int32" | "Decimal";
/** Description of a Property of a Page */
export interface IPageProperty {
    /** Name of the property */
    Name: string;

    /** Alias Name for the property (if set, or Name if not set, gets resolved to Name wOut AliasName on client) */
    AliasName: string;

    /** Title for "Merged" Visualization */
    AliasTitle: string;

    /** Title of the Property */
    Title: string;

    /** Type of the Property as .Net Type Name */
    Type: PagePropertyType;

    /** For Array Typed Properties : Description of Line Properties */
    ArrayProperties?: IPageMetaData & {
        /** Auf Welche Art sollen die Eigenschaften Visualisiert werden :
        - Default : als Grid
        - ForceGroup : als EigenschaftenGruppe */
        LineDisplayMode?: "Default" | "ForceGroup"
    };

    /** Set for Enum Types Only (and if at least one Mapping Available)*/
    EnumMappings?: IEnumMappings;

    /** Sync Mode for ReadMultiple Based Inatances */
    SyncMode?: NavSyncMode;

    /** Marker : Has Lookup (either static or dynamic=data dependent */
    HasLookup: boolean;

    /** if HasLookup= true and Lookup is Static (i.e. not dependent on current data)
     * (if not static, Server Callback with Current data is necessary to determine current lookup
     * '/infoma.Web.Nav/NavPageClient/GetLookupInfo')
    REMARK : Not set in NavPageClientController.GetMetaDataInfo on server side right now, as this leads to performance issues for first user after issreset,
    so is never set right now
     */
    StaticLookup?: ILookupInfo;

    /** Optional Custom Max Read in QuickLookup for lookup Fields only (overrides Default) */
    MaxRead?: number;

    /** Optional Custom MinSearch Input Length for QuickLookup to be triggered */
    MinSearch?: number;

    /** Visible */
    Invisible: boolean;

    /** Forced Readonly State */
    ReadOnly?: boolean;

    /** Grid Visibility */
    GridVisibility?: NavGridVisibility;

    /** Explicit Date Format in case of DateTime */
    DateFormat?: NavDtFormat;

    /** Explicit Detail Columns for Array Typed Properties (Grid) */
    DetailColumns?: string[];

    /** for Type = 'Decimal' */
    DecimalNumericType?: NumericType;

    /** Explicit AutoUpdate Behavior (default : on Page Customization, otherwise == true!)*/
    AutoUpdate?: boolean;

    HintField?: string;

    MaxLength?: number;

    LineCount?: number;

    CustomSearch?: {
        LookupField: string;
        LookupProperty: string;
    };

    /** At them Moment Custom Props on Invoice filtered on server for Security Reasons */
    IsCustomExclude?: boolean;
    //#region Client

    /** Unique Identification of a Property per PageMetaDataInfo Root (Name for Root Properties, RootProp.SubProb for Line Items) */
    Path: string;

    InitialVisible: boolean;

    /** For Line Properties: Parent Property  */
    ParentProp?: IPageProperty;

    /** Marker : Is Projected Users Enum */
    IsUsers?: boolean;
    //#endregion
}

/** Actual Objects are Class Oject over Serialized JSON */
export type IPageMetaData<Page = TDefaultPageObj, TPageName extends string = string> = _IPageMetaData<Page, TPageName> & NavPageMetaData<Page>;
/** Page MetaData Description */
interface _IPageMetaData<TPage = TDefaultPageObj, TPageName extends string = string> {

    /** Name of the Underlying Page */
    PageName: TPageName;

    /** Name of the Property (for array Sub- Metadata) */
    PropertyName?: string;

    /** Fields to Identify an Item by [to not use NAV 'Key' -> changes during server side manipulation ] ,
     * must be configured in Customization.xml ("ExplicitKey")
     */
    KeyFields?: string[];

    /** Default AutoUpdate Behavior, defaults to true in interpretation */
    DefaultAutoUpdate?: boolean;

    /** Information specific to Customized Root MetadData Descriptions */
    CustomInfo?: {
        InsertRowMethod?: string,
        DeleteRowMethod?: string,
        DuplicateRowMethod?: string
    };

    /** Properties of the Page */
    Properties: IPageProperty[];

    //#region custom client Side Generated Functionality (See BavClient.)

    /** Resolve Cient Key for a Model Item that corresponds to this Description */
    GetClientKey(data: any, noError: boolean): string;

    /** Get Kendo Column Config for given Property
    - Prefix : Optional Pre - Access Property Path
    - Editable : if true : Custom class 'idms_readonly' for non editable cells
    */
    GetColumn(p: IPageProperty, config?: { Prefix?: string, Editable?: boolean, SuppressHint?: boolean }): kendo.ui.GridColumn;

    /** Resolve Property By Name /Property Instance to 'Owned' Property Instance, opt. throwing Error if not available */
    ResolveProperty(prop: TPagePropertyAccess<TPage>, throwError?: boolean, useAlias?: boolean): IPageProperty;

    /** Model Typed genrated from Properties Information */
    Model: typeof Model;

    /** Container of Properties by "Name" */
    PropsByName: { [s in keyof TPage]: IPageProperty };

    AllPropsByPath: { [propName: string]: IPageProperty };

    /** For Line MetaData : Reference to Property on Parent */
    ParentProperty?: IPageProperty;

    /** For Line MetaData : Reference to Parent MetaData */
    Parent?: IPageMetaData;

    //#endregion
}

/** Lookup Description */
export interface ILookupInfo {
    /** Name of Lookup Definition */
    Name: string;
    /** Optional Explicit Title of Lookup */
    Title: string;
    /** Target Page Name */
    TargetPageName: string;
    /** Target Field name for TakeOver */
    TargetField: string;
    /** Search Field for User Input : Default Is TargetField */
    SearchField: string;
    /** Fields To display In DropDown */
    DropDownFields: Array<{ Name: string; Title?: string }>;
    /** Optional! Explicit Initial Display Fields settings migth be null! */
    DisplayFields?: Array<{ Name: string; Title?: string }>;
    /** Optional explicit Initial Search  Input Fields */
    DefaultSearchInputFields?: string[];
    /** Additional Constraints on Target Page */
    Constraints: IPageQueryConstraint[];
    /** Marker : the lookup Information is static i.e. unchangeable/not dependent on data */
    IsStatic: boolean;
    /** Custom Set Method (needed for multi Keyed Lookup Relations) */
    CustomSetMethod?: {
        Name: string,
        KeyName: string,
        Parameters: string[]
    };
    // Allow to set value wOut constraint also
    AllowAllValues?: boolean;
}

/** Parameters for server side resolve dynamic lookup */
export interface IDetermineDynamicLookup {
    NavUrl: string;
    PageName: string;
    CurrentItem: Model;
    LineProperty?: string;
    LineKey?: string;
    PropertyName: string;
}

/** "OnAfterGetLookup" Event Parameter */
export interface IDetermineDynamicLookupEvent {
    LookupInfo: ILookupInfo;
    DetermineLookup: IDetermineDynamicLookup;
}

/** Single Page Query Constraint Definition */
export interface IPageQueryConstraint<TPage = TDefaultPageObj> {
    FieldName: (keyof TPage) & string;

    Value: string; // msqqq : Enums : Array

    /**Client Handled : Op:
     * eq -> '=' + Value
     */
    Op?: string;
}

/** View Property Definition */
export interface IViewProperty {
    /** Name of the Property to Display */
    Name: string;

    /** Detail Properties (for Grid display) */
    Details?: IViewProperty[];

    /** Explicit Title for Display */
    Title?: string;

    /** with (client interpretation -> kendo grid column) */
    width?: string | number;

    /** Optionaly explicit default Hidden State for column */
    hidden?: boolean;
}

/** Definition of a View for an Item originating of a specific Page */
export interface IViewPageDisplay {
    /** Group Items to Display (--> NavPageItemPropertyGroup)  */
    Groups: IViewPropertyGroup[];

    /** PageName for View Definition */
    PageName: string;

    CustomBGColor?: string;

    /** Token (NAME) of the ViewConfiguration (for User Persistence etc) */
    ViewName: string;

    /** CLIENT Side determined Token for User Persistence of settings */
    UserPersistToken?: string[];

    /** Util "token" (as long as Same View we usually dont want to recreate UI Structure, Exception: UI-Designer(KWFAdmin)) */
    ChangeTracker?: any;
}

export interface IViewConfiguration {
    Name: string;
    Title: string;
    PerPage: { [pageName: string]: IViewPageDisplay };
}

export interface INavCustomizationInfo {
    /** Unique name for the Customization Info  */
    Name: string;
    /** Description */
    Description: string;
    /** (Exlicit) Url definitions for this Customization (others might get "defaulted") */
    NavUrls: Array<{ Url: string }>;
}

export interface IViewConfigurations {
    /** Marker : Configuration is Customization */
    IsCustomization: boolean;
    /** Reference of the Customization Config */
    Config: INavCustomizationInfo;
    /** Name of the Default View */
    DefaultView: string;
    /** Defined Views */
    Views: { [name: string]: IViewConfiguration };
    /** Defined Pages */
    Pages: { [name: string]: { PageName: string, Description: string, CustomBGColor?: string } };
}

/** Single View Configuration Item: Property Group Description  */
export interface IViewPropertyGroup {
    /** Unique Identifier */
    ID: string;

    /** Title of the Property Group */
    Title: string;

    /** Properties to Display in Group */
    Properties: IViewProperty[];

    /** Formula to dynamically Caclulate Visibility of item from Displayed Data */
    VisibilityFormula?: string;

    /** Default State for Collapsed Mode */
    InitialCollapsed?: boolean;

    /** Properties to be Propagated (TODO, not yet to be set, allow Configuration, ) */
    PropagateProperties?: IViewProperty[];
}

/** Custom Mandatory/Readonly Handing Callback */
export interface IPropertyInfoCallback {
    (config: {
        Item: NavPropertyItem;
        // Explicit State to Set.
        ExplicitState: Record<string, boolean>;
        // attached Property Container
        Container: NavPropertyContainer;
    }): void;
}
